import { useEffect, useRef } from "react";

import { CampaignSaveState } from "./useCampaignAutosave";

export interface LeaveDeletedCampaignArgs {
	/** The campaign open in the builder. */
	campaignUuid: string;
	/** The list's campaigns as the freshest list copy holds them; undefined until it has loaded. */
	campaigns: { id: string; title: string }[] | undefined;
	/** The builder's save state; `deleted` is the server refusing a save to a deleted campaign. */
	saveState: CampaignSaveState;
	/**
	 * Hold off while another dialog owns the campaign, such as the gear menu's settings with its own
	 * delete: leaving would remount that dialog on another campaign mid-delete. Checked again once
	 * it closes.
	 */
	paused?: boolean;
	/** Move to another campaign, or close the builder when `next` is null. */
	onLeave: (next: { id: string; title: string } | null) => void;
}

/**
 * The builder leaves a campaign that was deleted while it was open (PREC-4258): in another tab, by
 * a teammate or over MCP. Two signals say so: the list arrives without the campaign it once held
 * (the delete broadcasts it to the open builder), or a save is refused because the campaign was
 * deleted. Either way the builder moves to another campaign of the list, or closes when none is
 * left, so nothing keeps autosaving a campaign that is gone.
 *
 * A campaign only counts as gone after the list was seen holding it: a campaign created a moment
 * ago is open before the refetch that brings it in.
 */
export function useLeaveDeletedCampaign({
	campaignUuid,
	campaigns,
	saveState,
	paused = false,
	onLeave,
}: LeaveDeletedCampaignArgs): void {
	const seenRef = useRef(new Set<string>());
	const leftRef = useRef<string | null>(null);
	const onLeaveRef = useRef(onLeave);

	onLeaveRef.current = onLeave;

	useEffect(() => {
		const isListed = campaigns?.some((campaign) => campaign.id === campaignUuid) ?? false;

		if (isListed) {
			seenRef.current.add(campaignUuid);
		}

		const vanished = campaigns !== undefined && !isListed && seenRef.current.has(campaignUuid);

		if (paused || (!vanished && saveState !== "deleted") || leftRef.current === campaignUuid) {
			return;
		}

		leftRef.current = campaignUuid;
		onLeaveRef.current(campaigns?.find((campaign) => campaign.id !== campaignUuid) ?? null);
	}, [campaignUuid, campaigns, saveState, paused]);
}
